import { useEffect, useRef } from 'react';
import { get, isEmpty } from 'lodash';

import { useGetCurrentUser } from '@/api/generated';
import type { Candu } from '@/types/candu';

import { usePlatformPermissions } from './permissions/usePlatformPermissions';

/**
 * Returns the Candu instance from the global window object.
 * Since the script loads synchronously before React renders no need for reactive state tracking.
 */
export const useCandu = (): Candu | undefined => window.Candu;

export const CanduInit = () => {
  const candu = useCandu();
  const { value: platformPermissions } = usePlatformPermissions();
  const { data: currentUser } = useGetCurrentUser();
  const runOnceRef = useRef<boolean>(false);

  useEffect(() => {
    if (runOnceRef.current || !candu || isEmpty(currentUser) || isEmpty(platformPermissions)) {
      return;
    }

    const { id: userId, email } = currentUser;

    const fullName = get(
      currentUser,
      // eslint-disable-next-line i18next/no-literal-string
      'fullName',
      [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || ''
    );

    const initParams = {
      clientToken: import.meta.env.VITE_CANDU_CLIENT_TOKEN,
      userId,
      traits: { email, fullName, ...platformPermissions }
    };

    candu.init(initParams);
    runOnceRef.current = true;
  }, [platformPermissions, currentUser, candu]);

  return null;
};
